<template>
  <div class="layer-control-panel">
    <div class="panel-header">图层控制</div>
    <div class="panel-content">
      <div v-for="(info, type) in mappingTable" :key="type" class="layer-item"
        :class="{ 'is-hidden': !visibleLayers[type] }" @click="toggleLayerVisibility(type)">
        <div v-if="isColorIcon(info.icon)" class="layer-line-icon" :style="{ backgroundColor: getColor(info.icon) }">
        </div>
        <img v-else :src="getCachedIconUrl(info.icon)" class="layer-icon" />
        <span class="layer-name">{{ type }}</span>
        <a-icon :type="visibleLayers[type] ? 'eye' : 'eye-invisible'" class="visibility-toggle" />
      </div>
    </div>
  </div>
</template>

<script>
import { getImageBase64, getCachedImageBase64 } from '@/utils/imageUtils';

export default {
  name: 'LayerControl',
  props: {
    mappingTable: {
      type: Object,
      required: true
    },
    visibleLayers: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      iconCache: {} // 存储图标 URL 到 Base64 的映射
    };
  },
  watch: {
    // 监听 mappingTable 变化，预加载图标
    mappingTable: {
      immediate: true,
      handler () {
        this.preloadIcons();
      }
    }
  },
  methods: {
    // 判断是否为颜色图标（线图层）
    isColorIcon (icon) {
      return typeof icon === 'string' && icon.startsWith('color:');
    },
    // 获取颜色值
    getColor (icon) {
      return icon.split(':')[1];
    },
    // 预加载所有图标到缓存
    async preloadIcons () {
      if (!this.mappingTable) return;

      const iconUrls = [];
      Object.keys(this.mappingTable).forEach(key => {
        const mapping = this.mappingTable[key];
        if (mapping && mapping.icon) {
          const iconUrl = mapping.icon;
          // 只预加载 HTTP/HTTPS 图片
          if (iconUrl &&
            typeof iconUrl === 'string' &&
            !iconUrl.startsWith('color:') &&
            !iconUrl.startsWith('data:image') &&
            (iconUrl.startsWith('http://') || iconUrl.startsWith('https://'))) {
            iconUrls.push(iconUrl);
          }
        }
      });

      // 先同步全局缓存中已有的图标到本地缓存
      iconUrls.forEach(iconUrl => {
        const globalCached = getCachedImageBase64(iconUrl);
        if (globalCached && globalCached !== iconUrl) {
          this.$set(this.iconCache, iconUrl, globalCached);
        }
      });

      // 并行预加载未缓存的图标
      const preloadPromises = iconUrls
        .filter(iconUrl => !this.iconCache[iconUrl]) // 只预加载本地缓存中没有的
        .map(async (iconUrl) => {
          try {
            const cachedBase64 = await getImageBase64(iconUrl);
            if (cachedBase64 && cachedBase64 !== iconUrl) {
              // 使用 Vue.set 确保响应式更新
              this.$set(this.iconCache, iconUrl, cachedBase64);
            }
          } catch (e) {
            // 预加载失败不影响显示
          }
        });

      await Promise.all(preloadPromises);
    },
    // 获取图标URL，优先使用缓存（同步方法，用于模板）
    getCachedIconUrl (iconValue) {
      if (!iconValue) {
        // 返回一个透明的 1x1 像素占位符
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+';
      }

      // 如果已经是 base64，直接返回
      if (iconValue.startsWith('data:image')) {
        return iconValue;
      }

      // 如果是 HTTP/HTTPS URL，优先使用缓存
      if (iconValue.startsWith('http://') || iconValue.startsWith('https://')) {
        // 1. 先检查本地缓存
        if (this.iconCache[iconValue]) {
          return this.iconCache[iconValue];
        }

        // 2. 再检查全局缓存（EditingAmap 预加载的）
        const globalCached = getCachedImageBase64(iconValue);
        if (globalCached && globalCached !== iconValue) {
          // 同步到本地缓存，下次直接使用
          this.$set(this.iconCache, iconValue, globalCached);
          return globalCached;
        }

        // 3. 如果都没有，返回原始 URL（浏览器会使用 HTTP 缓存）
        return iconValue;
      }

      // 如果既不是 URL 也不是 base64，返回空占位符
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+';
    },
    // 切换图层显示隐藏
    toggleLayerVisibility (type) {
      const isVisible = !this.visibleLayers[type];
      // 派发事件让父组件更新状态和图层
      this.$emit('change', { type, isVisible });
    }
  }
}
</script>

<style lang="less" scoped>
.layer-control-panel {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1001;
  width: 240px;
  height: 30%;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  border: 1px solid #eee;

  .panel-header {
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    font-weight: bold;
    font-size: 14px;
    color: #333;
  }

  .panel-content {
    padding: 5px 0;
    overflow-y: auto;
    flex: 1;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 2px;
    }
  }

  .layer-item {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.3s;
    user-select: none;

    &:hover {
      background: rgba(64, 158, 255, 0.1);
    }

    &.is-hidden {
      opacity: 0.6;
      filter: grayscale(1);

      .layer-name {
        color: #999;
        text-decoration: line-through;
      }
    }

    .layer-icon {
      width: 18px;
      height: 18px;
      margin-right: 8px;
      object-fit: contain;
    }

    .layer-line-icon {
      width: 18px;
      height: 4px;
      margin-right: 8px;
      border-radius: 2px;
    }

    .layer-name {
      flex: 1;
      font-size: 13px;
      color: #666;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .visibility-toggle {
      font-size: 14px;
      color: #409EFF;
      margin-left: 5px;
    }
  }
}
</style>
